import React, { useContext } from 'react';
import { Tab } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroller';
import { useSelector } from 'react-redux';

import {
	getUnifiedLoadingState as getUnifiedLoadingStateSelector,
	getUnifiedPaginationSelector,
	getUnifiedSearchResults as getUnifiedSearchResultsSelector,
	SearchLoadingStatus,
} from '../../../../reducers/search.selectors';
import { selectUser } from '../../../../reducers/user.selectors';
import { trackingEvents } from '../../../../services/tracking/trackConfig';
import { MasterLayoutContext } from '../../../../state/MasterLayoutProvider';
import { SearchResultsTab } from '../../../../types/search';
import { hasNext } from '../../../../utils/pagination';
import useSearchExperiments from '../../../../utils/useSearchExperiments';
import MindItemMuiCard from '../../../cards/MuiCard/MindItemMuiCard/MindItemMuiCard';
import ProductMuiCard from '../../../cards/MuiCard/ProductMuiCard/ProductMuiCard';
import VideoMuiCard from '../../../cards/MuiCard/VideoMuiCard/VideoMuiCard';
import WithTracking from '../../../utils/WithTracking';
import RelatedTopicsPills, {
	RELATED_TOPICS_INLINE_INDEX,
} from '../../RelatedTopicsPills/RelatedTopicsPills';
import EmptySearchResultsNewUI from '../EmptySearchResultsNewUI';
import SearchResultsContainer from '../SearchResultsContainer';
import SearchResultsLoader from '../SearchResultsLoader';
import SearchResultsTopbarNewUI from '../SearchResultsTopbar';
import SingleSearchResultContainer from '../SingleSearchResultContainer';
import { getMindItemMuiCardProps } from './MindItemsTab';
import { getProductMuiCardProps } from './ProductsTab';
import type { SearchResultsTabProps } from './SearchResultsTab';
import { getVideoMuiCardProps } from './VideoMindItemsTab';

const UnifiedItemsTab = ({
	searchTerm,
	requestFilters,
	loadMore,
	searchEngineVersion,
	currentTab,
	onRelatedTopicSelect,
}: SearchResultsTabProps) => {
	const user = useSelector(selectUser);

	const unified = useSelector((state) => getUnifiedSearchResultsSelector(state));
	const unifiedLoadingState = useSelector(getUnifiedLoadingStateSelector);
	const unifiedPagination = useSelector(getUnifiedPaginationSelector);
	const { mainContentContainer } = useContext(MasterLayoutContext);
	const { useAiFilters } = useSearchExperiments();

	return user?.isCoreMember && currentTab === 'all' ? (
		<Tab.Pane eventKey={SearchResultsTab.ALL} data-testid="new-ui-all-tab">
			<SearchResultsTopbarNewUI
				total={unifiedPagination.total}
				searchTerm={searchTerm}
				isLoaded={unifiedLoadingState === SearchLoadingStatus.LOADED}
				requestFilters={requestFilters}
			/>
			{unifiedLoadingState === SearchLoadingStatus.INITIAL ||
			unifiedLoadingState === SearchLoadingStatus.NONE ? (
				<SearchResultsLoader />
			) : (
				<>
					{unified.length > 0 ? (
						<InfiniteScroll
							loadMore={loadMore}
							hasMore={
								hasNext(unifiedPagination) && unifiedLoadingState !== SearchLoadingStatus.PAGINATION
							}
							className="search-results__infinite-scroll-box"
							useWindow={false}
							getScrollParent={() => mainContentContainer}
						>
							<SearchResultsContainer>
								{unified.map((item: any, index) => (
									<React.Fragment key={item.id}>
										<SingleSearchResultContainer>
											{item.fileType === 'video' ? (
												<WithTracking
													events={{
														onClick: trackingEvents.itemOpen,
														onMiddleClick: trackingEvents.itemOpen,
														onContextMenu: trackingEvents.itemOpen,
													}}
													eventPayload={{
														other: {
															minditem_id: item.id,
															index,
															searchEngineVersion,
														},
													}}
												>
													<div>
														<VideoMuiCard {...getVideoMuiCardProps(item, useAiFilters)} index={index} />
													</div>
												</WithTracking>
											) : item.fileType === 'document' ? (
												<WithTracking
													events={{
														onClick: trackingEvents.itemOpen,
														onMiddleClick: trackingEvents.itemOpen,
														onContextMenu: trackingEvents.itemOpen,
													}}
													eventPayload={{
														other: {
															minditem_id: item.id,
															index,
															searchEngineVersion,
														},
													}}
												>
													<div>
														<MindItemMuiCard {...getMindItemMuiCardProps(item, useAiFilters)} index={index} />
													</div>
												</WithTracking>
											) : (
												<WithTracking
													events={{
														onClick: trackingEvents.itemOpen,
														onMiddleClick: trackingEvents.itemOpen,
														onContextMenu: trackingEvents.itemOpen,
													}}
													eventPayload={{
														other: {
															product_id: item.id,
															index,
															searchEngineVersion,
														},
													}}
												>
													<div>
														<ProductMuiCard {...getProductMuiCardProps(item, useAiFilters)} index={index} />
													</div>
												</WithTracking>
											)}
										</SingleSearchResultContainer>
										{index === RELATED_TOPICS_INLINE_INDEX && onRelatedTopicSelect && (
											<RelatedTopicsPills variant="inline" term={searchTerm} onSelect={onRelatedTopicSelect} />
										)}
									</React.Fragment>
								))}
							</SearchResultsContainer>
						</InfiniteScroll>
					) : (
						<SearchResultsContainer>
							<EmptySearchResultsNewUI searchTerm={searchTerm} />
						</SearchResultsContainer>
					)}
					{unifiedLoadingState === SearchLoadingStatus.PAGINATION && <SearchResultsLoader />}
				</>
			)}
		</Tab.Pane>
	) : null;
};

export default UnifiedItemsTab;
